import React from 'react';
import {
  Paper,
  Typography,
  IconButton,
  Chip,
  Box
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { THEME_COLORS, TYPOGRAPHY, ANIMATIONS } from '../constants';

/**
 * Composant pour afficher une carte de participant moderne
 */
const ParticipantCard = ({ participant, index, onRemove }) => {
  // Déterminer le thème couleur selon le type
  const isAccompanist = participant.type === 'Accompagnateur';
  const themeColor = isAccompanist ? THEME_COLORS.primary : THEME_COLORS.secondary;

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: { xs: 2, sm: 3 },
        background: THEME_COLORS.background.paper,
        border: `1px solid ${themeColor.main}20`,
        position: 'relative',
        transition: ANIMATIONS.transition.normal,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        '&:hover': {
          transform: { xs: 'none', sm: 'translateY(-2px)' },
          boxShadow: { xs: THEME_COLORS.shadow.md, sm: `0 8px 24px ${themeColor.main}20` },
          borderColor: `${themeColor.main}30`
        }
      }}
    >
      {/* Header responsive */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        mb: { xs: 2, sm: 3 },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 1, sm: 0 }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
          {/* Numéro plus fin */}
          <Box
            sx={{
              width: { xs: 32, sm: 36 },
              height: { xs: 32, sm: 36 },
              borderRadius: { xs: 2, sm: 3 },
              background: themeColor.gradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 2px 8px ${themeColor.main}25`
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: 'white',
                fontWeight: TYPOGRAPHY.fontWeight.bold,
                fontFamily: TYPOGRAPHY.fontFamily.primary,
                fontSize: { xs: TYPOGRAPHY.fontSize.sm, sm: TYPOGRAPHY.fontSize.base }
              }}
            >
              #{index + 1}
            </Typography>
          </Box>

          {/* Badge plus fin */}
          
        </Box>

        {/* Bouton de suppression plus fin */}
        <IconButton
          onClick={() => onRemove(participant.id)}
          sx={{
            backgroundColor: THEME_COLORS.background.main,
            color: THEME_COLORS.error.main,
            boxShadow: THEME_COLORS.shadow.sm,
            transition: ANIMATIONS.transition.normal,
            width: { xs: 32, sm: 36 },
            height: { xs: 32, sm: 36 },
            alignSelf: { xs: 'flex-end', sm: 'flex-start' },
            '&:hover': {
              backgroundColor: THEME_COLORS.error.main,
              color: 'white',
              transform: { xs: 'none', sm: 'scale(1.05)' },
              boxShadow: `0 4px 16px ${THEME_COLORS.error.main}30`
            }
          }}
          size="small"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Informations plus compactes avec flex-grow */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: { xs: 1.5, sm: 2 },
        flex: 1,
        justifyContent: 'flex-start'
      }}>
        <Chip
            label={participant.type}
            icon={
              <Typography sx={{ fontSize: { xs: '0.8rem', sm: '1rem' }, mr: 0.5 }}>
                {isAccompanist ? '👥' : '🪖'}
              </Typography>
            }
            sx={{
              background: themeColor.gradient,
              color: 'white',
              fontWeight: TYPOGRAPHY.fontWeight.semibold,
              fontFamily: TYPOGRAPHY.fontFamily.primary,
              fontSize: { xs: TYPOGRAPHY.fontSize.xs, sm: TYPOGRAPHY.fontSize.sm },
              borderRadius: { xs: 1.5, sm: 2 },
              height: { xs: 24, sm: 28 },
              px: { xs: 0.5, sm: 1 },
              '& .MuiChip-label': {
                px: { xs: 0.5, sm: 1 }
              }
            }}
          />
        {/* Nom complet */}
        {participant.firstName && participant.lastName && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }}>👤</Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: TYPOGRAPHY.fontWeight.semibold,
                fontFamily: TYPOGRAPHY.fontFamily.primary,
                color: THEME_COLORS.text.primary,
                fontSize: { xs: TYPOGRAPHY.fontSize.base, sm: TYPOGRAPHY.fontSize.base },
                lineHeight: 1.3
              }}
            >
              {participant.firstName} {participant.lastName}
            </Typography>
          </Box>
        )}

        {/* Email */}
        {participant.email && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>📧</Typography>
            <Typography
              variant="body1"
              sx={{
                fontFamily: TYPOGRAPHY.fontFamily.primary,
                color: THEME_COLORS.text.secondary,
                fontSize: { xs: TYPOGRAPHY.fontSize.sm, sm: TYPOGRAPHY.fontSize.sm },
                wordBreak: 'break-word'
              }}
            >
              {participant.email}
            </Typography>
          </Box>
        )}

        {/* Téléphone */}
        {participant.phone && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>📞</Typography>
            <Typography
              variant="body1"
              sx={{
                fontFamily: TYPOGRAPHY.fontFamily.primary,
                color: THEME_COLORS.text.secondary,
                fontSize: { xs: TYPOGRAPHY.fontSize.sm, sm: TYPOGRAPHY.fontSize.base }
              }}
            >
              {participant.phone}
            </Typography>
          </Box>
        )}

        {/* Indicateur lead plus fin */}
        {!isAccompanist && participant.leadId && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: { xs: 0.5, sm: 1 } }}>
            <Chip
              label="✅ Profil trouvé"
              size="small"
              sx={{
                backgroundColor: THEME_COLORS.success.main + '15',
                color: THEME_COLORS.success.main,
                fontFamily: TYPOGRAPHY.fontFamily.primary,
                fontWeight: TYPOGRAPHY.fontWeight.medium,
                fontSize: { xs: '0.6rem', sm: TYPOGRAPHY.fontSize.xs },
                height: { xs: 20, sm: 24 }
              }}
            />
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default ParticipantCard;